import { AfterViewInit, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { EventService } from '../core/service/event.service';
import { LAYOUT_DETACHED, LAYOUT_HORIZONTAL, LAYOUT_TWO_COLUMN_MENU, LAYOUT_VERTICAL, LAYOUT_WIDTH_BOXED, LEFT_SIDEBAR_TYPE_CONDENSED, LEFT_SIDEBAR_TYPE_DEFAULT } from './shared/config/layout.model';
import { getLayoutConfig } from './shared/helper/utils';
import { LayoutConfig } from './shared/models/layout-config.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { selectSubscription } from '../modules/shared/store/store.selectors';

// Bump this whenever there is a new batch of changes to announce — the modal
// will then reappear once for every manager until they dismiss it again.
const WHATS_NEW_VERSION = '2026-09';
const WHATS_NEW_VISIBLE_UNTIL = new Date(2026, 9, 5);
const WHATS_NEW_STORAGE_KEY = 'fodimi_whats_new_version';

@Component({
  selector: 'app-layout-container',
  templateUrl: './layout-container.component.html',
  styleUrls: ['./layout-container.component.scss']
})
export class LayoutContainerComponent implements OnInit, AfterViewInit {

  // layout related config
  layoutType!: string;
  layoutConfig!: LayoutConfig;
  layoutWidth!: string;
  configuredDemo: string = "default";

  // "What's new" announcement
  @ViewChild('whatsNewModal') whatsNewModal!: TemplateRef<any>;
  hasKiosk = false;
  hasInventory = false;
  private whatsNewOpened = false;
  private subscription$?: Subscription;

  constructor (
      private eventService: EventService,
      private modalService: NgbModal,
      private store: Store,
  ) { }

  ngOnInit(): void {
    // default settings
    this.configuredDemo = 'demo';

    // tslint:disable-next-line: max-line-length
    this.layoutType = this.configuredDemo === 'creative' ? LAYOUT_HORIZONTAL : (this.configuredDemo === 'modern' ? LAYOUT_DETACHED : (this.configuredDemo === 'saas' ? LAYOUT_TWO_COLUMN_MENU : LAYOUT_VERTICAL));
    this.setLayoutConfig();

    // listen to event and change the layout configuarations
    this.eventService.subscribe('changeLayout', (layout) => {
      this.layoutType = layout;
      this.setLayoutConfig();
    });

    this.eventService.subscribe('changeLayoutWidth', (width) => {
      setTimeout(() => {
        this.layoutWidth = width;
        if (this.layoutWidth === LAYOUT_WIDTH_BOXED) {
          this.eventService.broadcast('changeLeftSidebarType', LEFT_SIDEBAR_TYPE_CONDENSED);
        } else {
          this.eventService.broadcast('changeLeftSidebarType', LEFT_SIDEBAR_TYPE_DEFAULT);
        }
      }, 20);
    });


    this.updateDimensions();

  }

  ngAfterViewInit(): void {
    document.body.classList.remove('authentication-bg', 'authentication-bg-pattern', 'auth-fluid-pages', 'pb-0');
    this.maybeShowWhatsNew();
  }

  /**
   * set layout config
   */
  setLayoutConfig(): void {
    this.layoutConfig = getLayoutConfig(this.layoutType);
    this.layoutWidth = this.layoutConfig.layoutWidth;
  }

  /**
 * changes left sidebar type based on screen dimensions
 */
  updateDimensions(): void {
    if (this.layoutType !== LAYOUT_TWO_COLUMN_MENU) {
      if (window.innerWidth >= 768 && window.innerWidth <= 1028) {
        this.eventService.broadcast('changeLeftSidebarType', LEFT_SIDEBAR_TYPE_CONDENSED);
      }
      else if (window.innerWidth > 1028) {
        this.eventService.broadcast('changeLeftSidebarType', LEFT_SIDEBAR_TYPE_DEFAULT);
      }
    }
  }

  /**
   * Show the "What's new" modal once per release, after the subscription is
   * known (so feature-specific items like the kiosk can be shown conditionally).
   */
  private maybeShowWhatsNew(): void {
    if (new Date() >= WHATS_NEW_VISIBLE_UNTIL || localStorage.getItem(WHATS_NEW_STORAGE_KEY) === WHATS_NEW_VERSION) {
      return;
    }

    this.subscription$ = this.store.select(selectSubscription).subscribe(subscription => {
      if (this.whatsNewOpened || !subscription) {
        return;
      }
      this.hasKiosk = !!subscription.KIOSK;
      this.hasInventory = !!subscription.INVENTORY;
      this.openWhatsNew();
    });

    // Fallback: show even if the subscription never resolves.
    setTimeout(() => this.openWhatsNew(), 4000);
  }

  private openWhatsNew(): void {
    if (this.whatsNewOpened || !this.whatsNewModal) {
      return;
    }
    this.whatsNewOpened = true;
    this.subscription$?.unsubscribe();

    const markSeen = () => localStorage.setItem(WHATS_NEW_STORAGE_KEY, WHATS_NEW_VERSION);
    const ref = this.modalService.open(this.whatsNewModal, {
      size: 'lg',
      centered: true,
      scrollable: true,
      windowClass: 'whats-new-modal',
    });
    ref.result.then(markSeen, markSeen);
  }
}
